/**
 * Platform-admin seam: businesses, resellers, applications, users, payments,
 * health, webhooks, cash-entry. Wraps the mock consts behind hooks so the
 * mock→real flip is an env change (VITE_USE_MOCKS).
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGet, apiGetList, apiPost, apiPatch, apiPut, apiDelete, apiFetchRaw } from './client';
import {
  adminBusinesses, resellers, resellerApplications, pendingApplicationsCount,
  adminUsers, adminPayments, packageSubscribers, webhookDeliveries, cashEntryCandidate, resellerPayments,
  platformKpis, platformActivity, topResellers, businessesByStatus,
  platformPricingTiers, resellerSubscriptions, resellerPackages, resellerPayouts,
  resellerPayoutConfig, resellerFloorOverrides,
} from '../mocks/admin';
import type {
  ResellerSubscription, ResellerPayment, ResellerPackage, ResellerPayout,
} from '../mocks/admin';

/** A flat list row carries its parent reseller id (the api shape), e.g. ResellerSubscription & WithReseller. */
type WithReseller = { resellerId: string };

/**
 * Flatten a `Record<resellerId, T[]>` fixture into the flat list the real API
 * returns from `GET /admin/<collection>`, injecting `resellerId` onto each row.
 * Replaces the old "return the whole map, index by id in the component" shape.
 */
function flattenByReseller<T>(map: Record<string, T[]>): Array<T & WithReseller> {
  return Object.entries(map).flatMap(([resellerId, items]) => items.map((i) => ({ ...i, resellerId })));
}
import { auditLog } from '../mocks/audit';
import { resellerStaff as mockResellerStaff, resellerRevenue } from '../mocks/reseller';
import { packages as mockPackages, pricesByPackage as mockPricesByPackage, type Price as CatalogPrice } from '../mocks/pricing';

export const getAuditLog = (resellerId?: string) =>
  USE_MOCKS
    ? mockDelay(auditLog)
    : apiGetList<(typeof auditLog)[number]>(resellerId ? `/admin/audit?resellerId=${encodeURIComponent(resellerId)}` : '/admin/audit');
export const useAuditLog = (resellerId?: string) =>
  useQuery({ queryKey: ['admin', 'audit', resellerId ?? 'all'], queryFn: () => getAuditLog(resellerId) });

export const listAdminBusinesses = () =>
  USE_MOCKS ? mockDelay(adminBusinesses) : apiGetList<(typeof adminBusinesses)[number]>('/admin/businesses');
export const listResellers = () =>
  USE_MOCKS ? mockDelay(resellers) : apiGetList<(typeof resellers)[number]>('/admin/resellers');
export const listResellerApplications = () =>
  USE_MOCKS ? mockDelay(resellerApplications) : apiGetList<(typeof resellerApplications)[number]>('/admin/resellers/applications');
/** One application in FULL (+ timeline) — the review drawer's real source.
 *  The list DTO carries only what the table shows; rendering the drawer from a list row is
 *  what made `timeline` undefined and crashed on `entries.length`. */
export const getResellerApplication = (id: string) =>
  USE_MOCKS
    ? mockDelay(resellerApplications.find((a) => a.id === id) ?? resellerApplications[0]!)
    : apiGet<(typeof resellerApplications)[number]>(`/admin/reseller-applications/${id}`);
export const getPendingApplicationsCount = () =>
  USE_MOCKS ? mockDelay(pendingApplicationsCount) : apiGet<typeof pendingApplicationsCount>('/admin/resellers/applications/count');
export const listAdminUsers = () =>
  USE_MOCKS ? mockDelay(adminUsers) : apiGetList<(typeof adminUsers)[number]>('/admin/users');
export const listAdminPayments = () =>
  USE_MOCKS ? mockDelay(adminPayments) : apiGetList<(typeof adminPayments)[number]>('/admin/payments');
export const listPackageSubscribers = (packageId: string) =>
  USE_MOCKS ? mockDelay(packageSubscribers) : apiGetList<(typeof packageSubscribers)[number]>(`/admin/packages/${packageId}/subscribers`);
/** Real /admin/health payload — live checks, no fabricated services. */
export type SystemHealthService = { name: string; status: 'ok' | 'degraded' | 'down' | 'unknown'; detail: string };
export type SystemHealthPayload = {
  services: SystemHealthService[];
  queueDepth: Record<string, number>;
  databaseDistribution: { shared: number; managed: number; selfHosted: number; legacy: number };
  billingCronLastRunAt: string | null;
  allNominal: boolean;
};
const MOCK_SYSTEM_HEALTH: SystemHealthPayload = {
  services: [
    { name: 'API', status: 'ok', detail: 'up 12h 4m' },
    { name: 'PostgreSQL (control plane)', status: 'ok', detail: '3 ms' },
    { name: 'Email outbox', status: 'ok', detail: '0 pending' },
    { name: 'Webhook dispatch', status: 'ok', detail: '0 undelivered' },
    { name: 'Billing cron', status: 'ok', detail: 'last run 6h ago' },
    { name: 'MariaDB (POS)', status: 'unknown', detail: 'not monitored from this environment' },
  ],
  queueDepth: { emails: 0, webhooks: 0, provisioning: 1 },
  databaseDistribution: { shared: 3, managed: 2, selfHosted: 0, legacy: 110 },
  billingCronLastRunAt: null,
  allNominal: true,
};
export const getSystemHealth = () =>
  USE_MOCKS ? mockDelay(MOCK_SYSTEM_HEALTH) : apiGet<SystemHealthPayload>('/admin/health');
export const listWebhookDeliveries = () =>
  USE_MOCKS ? mockDelay(webhookDeliveries) : apiGetList<(typeof webhookDeliveries)[number]>('/admin/webhooks');
/** Cash-entry candidate search — real owed balance per matching business. */
export type CashCandidate = {
  id: string;
  name: string;
  plan: string | null;
  status: string;
  pastDueSince: string | null;
  owedMinor: number;
  currencyCode: string;
};
const MOCK_CASH_CANDIDATES: CashCandidate[] = [
  {
    id: cashEntryCandidate.business.id,
    name: cashEntryCandidate.business.name,
    plan: cashEntryCandidate.business.plan,
    status: cashEntryCandidate.business.status,
    pastDueSince: cashEntryCandidate.business.pastDueSince,
    owedMinor: cashEntryCandidate.business.owedMinor,
    currencyCode: 'BDT',
  },
];
export const searchCashCandidates = (q: string) =>
  USE_MOCKS
    ? mockDelay(MOCK_CASH_CANDIDATES.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase())))
    : apiGetList<CashCandidate>(`/admin/cash-entry?q=${encodeURIComponent(q.trim())}`);
export const getResellerPayments = () =>
  USE_MOCKS
    ? mockDelay(flattenByReseller(resellerPayments))
    : apiGetList<ResellerPayment & WithReseller>('/admin/reseller-payments');
export type PlatformKpis = typeof platformKpis & {
  businessesThisWeek?: number;
  pastDueBusinesses?: number;
  pendingApplications?: number;
};
export const getPlatformKpis = () =>
  USE_MOCKS ? mockDelay(platformKpis as PlatformKpis) : apiGet<PlatformKpis>('/admin/kpis');
export const getPlatformActivity = () =>
  USE_MOCKS ? mockDelay(platformActivity) : apiGetList<(typeof platformActivity)[number]>('/admin/activity');
export const getTopResellers = () =>
  USE_MOCKS ? mockDelay(topResellers) : apiGetList<(typeof topResellers)[number]>('/admin/resellers/top');
export const getBusinessesByStatus = () =>
  USE_MOCKS ? mockDelay(businessesByStatus) : apiGetList<(typeof businessesByStatus)[number]>('/admin/businesses/by-status');
export const listPlatformPricingTiers = () =>
  USE_MOCKS ? mockDelay(platformPricingTiers) : apiGetList<(typeof platformPricingTiers)[number]>('/admin/pricing-tiers');
export const getResellerSubscriptions = () =>
  USE_MOCKS
    ? mockDelay(flattenByReseller(resellerSubscriptions))
    : apiGetList<ResellerSubscription & WithReseller>('/admin/reseller-subscriptions');
export const getResellerPackages = () =>
  USE_MOCKS
    ? mockDelay(flattenByReseller(resellerPackages))
    : apiGetList<ResellerPackage & WithReseller>('/admin/reseller-packages');
export const getResellerPayouts = () =>
  USE_MOCKS
    ? mockDelay(flattenByReseller(resellerPayouts))
    : apiGetList<ResellerPayout & WithReseller>('/admin/reseller-payouts');
export const getResellerPayoutConfig = () =>
  USE_MOCKS ? mockDelay(resellerPayoutConfig) : apiGet<typeof resellerPayoutConfig>('/admin/reseller-payout-config');
/** Flat API rows → the page's map shape: { [resellerId]: { [tierSlug]: override } }. */
type FloorOverrideApiRow = { resellerId: string; resellerName: string; tierSlug: string; minMonthlyMinor: number | null; minYearlyMinor: number | null };
export const getResellerFloorOverrides = async (): Promise<typeof resellerFloorOverrides> => {
  if (USE_MOCKS) return mockDelay(resellerFloorOverrides);
  const rows = await apiGetList<FloorOverrideApiRow>('/admin/reseller-floor-overrides');
  const map: Record<string, Record<string, { minMonthlyMinor: number; minYearlyMinor: number; setAt: string; setByEmail: string }>> = {};
  for (const row of rows) {
    (map[row.resellerId] ??= {})[row.tierSlug] = {
      minMonthlyMinor: row.minMonthlyMinor ?? 0,
      minYearlyMinor: row.minYearlyMinor ?? 0,
      setAt: '',
      setByEmail: '',
    };
  }
  return map as unknown as typeof resellerFloorOverrides;
};
/** Set / clear one (reseller, tier) floor override. Money in MINOR units. */
export const setResellerFloorOverride = (resellerId: string, tierSlug: string, floors: { minMonthlyMinor: number; minYearlyMinor: number }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPut(`/admin/resellers/${resellerId}/floor-overrides/${tierSlug}`, floors);
export const deleteResellerFloorOverride = (resellerId: string, tierSlug: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/admin/resellers/${resellerId}/floor-overrides/${tierSlug}`);
/** Admin edit of a reseller's payout settlement details. */
export const setResellerPayoutConfig = (resellerId: string, payout: Record<string, string>) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/resellers/${resellerId}/payout-config`, payout);

// ── Admin domains (cross-platform list of every reseller + business domain) ──
export type AdminDomain = {
  id: string;
  hostname: string;
  scope: 'reseller' | 'business';
  ownerName: string;
  isPrimary: boolean;
  isVerified: boolean;
  tlsStatus: 'issued' | 'pending' | 'failed';
  addedAt: string;
};
const MOCK_ADMIN_DOMAINS: AdminDomain[] = [
  { id: 'd1', hostname: 'acme.itcitysoft.com', scope: 'reseller', ownerName: 'Acme Resellers', isPrimary: true, isVerified: true, tlsStatus: 'issued', addedAt: '2026-01-12' },
  { id: 'd2', hostname: 'acme-resellers.com', scope: 'reseller', ownerName: 'Acme Resellers', isPrimary: false, isVerified: true, tlsStatus: 'issued', addedAt: '2026-01-14' },
  { id: 'd3', hostname: 'sundarbn.itcitysoft.com', scope: 'reseller', ownerName: 'Sundarban Tech', isPrimary: true, isVerified: true, tlsStatus: 'issued', addedAt: '2025-11-04' },
  { id: 'd4', hostname: 'citypos.itcitysoft.com', scope: 'reseller', ownerName: 'CityPOS Bangla', isPrimary: true, isVerified: true, tlsStatus: 'issued', addedAt: '2025-09-22' },
  { id: 'd5', hostname: 'pos.acme-stores.com', scope: 'business', ownerName: 'Acme Main Store', isPrimary: false, isVerified: true, tlsStatus: 'issued', addedAt: '2026-01-15' },
  { id: 'd6', hostname: 'sky.itcitysoft.com', scope: 'business', ownerName: 'Sky Pharmacy', isPrimary: true, isVerified: true, tlsStatus: 'issued', addedAt: '2026-02-04' },
  { id: 'd7', hostname: 'shop.greenfields.bd', scope: 'business', ownerName: 'Greenfields Mart', isPrimary: false, isVerified: false, tlsStatus: 'pending', addedAt: '2026-05-03' },
  { id: 'd8', hostname: 'pos.khan-bakery.com', scope: 'business', ownerName: 'Khan Bakery', isPrimary: false, isVerified: false, tlsStatus: 'failed', addedAt: '2026-05-01' },
];
export const listAdminDomains = () =>
  USE_MOCKS ? mockDelay(MOCK_ADMIN_DOMAINS) : apiGetList<AdminDomain>('/admin/domains');
export const useAdminDomains = () => useQuery({ queryKey: ['admin', 'domains'], queryFn: listAdminDomains });

// ── One reseller's white-label domains (admin ResellerDetail → Domains tab) ──
export type AdminResellerDomain = {
  id: string;
  host: string;
  isPrimary: boolean;
  dnsStatus: 'pending' | 'verified' | 'failed';
  tlsStatus: 'pending' | 'issued' | 'failed';
  verifiedAt: string | null;
};
const MOCK_RESELLER_DOMAINS: AdminResellerDomain[] = [
  { id: 'd1', host: 'acme.itcitysoft.com', isPrimary: true, dnsStatus: 'verified', tlsStatus: 'issued', verifiedAt: '2026-01-12' },
  { id: 'd2', host: 'acme-resellers.com', isPrimary: false, dnsStatus: 'verified', tlsStatus: 'issued', verifiedAt: '2026-01-14' },
  { id: 'd3', host: 'pos.acme-stores.com', isPrimary: false, dnsStatus: 'pending', tlsStatus: 'pending', verifiedAt: null },
];
export const listAdminResellerDomains = (id: string) =>
  USE_MOCKS ? mockDelay(MOCK_RESELLER_DOMAINS) : apiGetList<AdminResellerDomain>(`/admin/resellers/${id}/domains`);
export const useAdminResellerDomains = (id: string) =>
  useQuery({ queryKey: ['admin', 'resellerDomains', id], queryFn: () => listAdminResellerDomains(id), enabled: !!id });

// A specific reseller's staff (admin ResellerDetail Staff tab) — keyed by reseller id.
export const getResellerStaffAdmin = (id: string) =>
  USE_MOCKS ? mockDelay(mockResellerStaff) : apiGetList<(typeof mockResellerStaff)[number]>(`/admin/resellers/${id}/staff`);
export const useResellerStaffAdmin = (id: string) =>
  useQuery({ queryKey: ['admin', 'resellerStaff', id], queryFn: () => getResellerStaffAdmin(id), enabled: !!id });

// Create a platform-owned package (admin Packages "New package" drawer).
export type NewAdminPackageInput = {
  name: string;
  internalCode?: string;
  tagline?: string;
  categoryId?: string;
  monthlyMinor: number;
  yearlyMinor: number;
  status: 'draft' | 'published';
  /** false = PRIVATE plan. The flag existed on the EDIT page only, so a new package was
   *  always public until someone went back and edited it — the window in between is
   *  exactly when a half-built plan is visible on the pricing page. */
  isPublic?: boolean;
  features: { locations: number | 'unlimited'; users: number | 'unlimited'; products: number | 'unlimited'; invoices: number | 'unlimited' };
};
export const addAdminPackage = (input: NewAdminPackageInput) =>
  USE_MOCKS ? mockDelay({ id: 'pkg_new', name: input.name, ok: true }) : apiPost<{ id: string; name: string; ok: boolean }>('/admin/packages', input);

// Admin Packages list: ALL packages/prices in any status (the public GET /packages
// is published-only, which makes the page's Draft/Deprecated/Archived filters dead).
/** An admin catalog row: the pricing mock's shape plus WHICH catalog it sells from
 *  (`catalogResellerId` null = platform). */
export type AdminCatalogPackage = (typeof mockPackages)[number] & { catalogResellerId?: string | null; catalogName?: string | null };
export const listAdminAllPackages = () =>
  USE_MOCKS ? mockDelay<AdminCatalogPackage[]>(mockPackages) : apiGetList<AdminCatalogPackage>('/admin/packages');
export const useAdminAllPackages = () => useQuery({ queryKey: ['admin', 'allPackages'], queryFn: listAdminAllPackages });
export const listAdminAllPrices = () =>
  USE_MOCKS ? mockDelay(Object.values(mockPricesByPackage).flat()) : apiGetList<CatalogPrice>('/admin/prices');
export const useAdminAllPrices = () => useQuery({ queryKey: ['admin', 'allPrices'], queryFn: listAdminAllPrices });

// Full editable detail for one package (admin PackageEditor) — GET/PATCH /admin/packages/:id.
export type AdminPackageDetail = {
  id: string;
  internalCode: string;
  name: string;
  tagline: string;
  status: 'draft' | 'published' | 'deprecated' | 'archived';
  /** "View publicly" (#34): false = private plan — off every public pricing page
   *  and self-serve picker, still assignable by an admin or reseller. */
  isPublic: boolean;
  description: string;
  marketingBadge: string;
  ownedBy: 'platform' | 'reseller';
  limits: { locations: number | 'unlimited'; users: number | 'unlimited'; products: number | 'unlimited'; invoices: number | 'unlimited'; invoicesYearly?: number | 'unlimited' };
  modules: string[];
  prices: CatalogPrice[];
};
const MOCK_PACKAGE_DETAIL: AdminPackageDetail = {
  id: 'pkg_pro', internalCode: 'plat_pro', name: 'Pro', tagline: 'For growing businesses',
  status: 'published', isPublic: true, description: 'Up to 5 locations, 15 users, dedicated DB.', marketingBadge: 'most-popular',
  ownedBy: 'platform',
  limits: { locations: 5, users: 15, products: 10000, invoices: 'unlimited' },
  modules: ['CRM', 'Essentials'], prices: [],
};
export const getAdminPackage = (id: string) =>
  USE_MOCKS ? mockDelay(MOCK_PACKAGE_DETAIL) : apiGet<AdminPackageDetail>(`/admin/packages/${id}`);
export const useAdminPackage = (id: string) =>
  useQuery({ queryKey: ['admin', 'package', id], queryFn: () => getAdminPackage(id), enabled: !!id });
export type AdminPackagePatch = Partial<Pick<AdminPackageDetail, 'name' | 'tagline' | 'status' | 'isPublic' | 'description' | 'marketingBadge' | 'limits' | 'modules'>>;
export const updateAdminPackage = (id: string, patch: AdminPackagePatch) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch<{ ok: boolean }>(`/admin/packages/${id}`, patch);
/** Activate a draft/archived price (auto-archives same-interval active siblings) or archive an active one. */
export const setPackagePriceStatus = (packageId: string, priceId: string, status: 'active' | 'archived') =>
  USE_MOCKS
    ? mockDelay({ ok: true, archivedSiblings: 0 })
    : apiPatch<{ ok: boolean; archivedSiblings: number }>(`/admin/packages/${packageId}/prices/${priceId}`, { status });

export const useAdminBusinesses = () => useQuery({ queryKey: ['admin', 'businesses'], queryFn: listAdminBusinesses });

/** Full business drill-in (GET /admin/businesses/:id). Real API only — no mock detail. */
export type AdminBusinessDetail = {
  id: string;
  name: string;
  status: string;
  dbMode: string;
  paymentReviewAt: string | null;
  createdAt: string;
  ownerEmail: string;
  resellerName: string | null;
  resellerId: string | null;
  legacyDb: string | null;
  laravelBusinessId: number | null;
  database: { mode: string; status: string; dbName: string; host: string; port: number; provisionedAt: string | null; lastError: string | null; attemptCount: number } | null;
  subscription: {
    status: string; plan: string | null; currentPeriodEnd: string | null; unitAmountMinor: number; interval: string;
    trialEndsAt: string | null; startedAt: string | null;
    /** Live "used" vs the plan's cap (limit 0 = unlimited/unset). Same source as the owner's page. */
    usage?: {
      locations: { used: number; limit: number };
      users: { used: number; limit: number };
      products: { used: number; limit: number };
    };
    /** Frozen entitlements resolved snapshot → linked package (null cap = unlimited). */
    entitlements?: {
      maxLocations: number | null;
      maxUsers: number | null;
      maxProducts: number | null;
      maxInvoices: number | null;
      maxInvoicesYearly: number | null;
      modules: string[];
    };
    /** What happens next while an invoice is unpaid, and on exactly which dates. Computed
     *  server-side from the same MILESTONES ladder the dunning cron enforces — never
     *  re-derive these in the UI. Null when nothing is owed. */
    dunning?: {
      invoiceNo: string;
      dueAt: string;
      amountDueMinor: number;
      daysOverdue: number;
      suspendsOn: string;
      cancelsOn: string;
      daysUntilSuspend: number;
      suspended: boolean;
    } | null;
  } | null;
  payments: Array<{ id: string; status: string; amountMinor: number; method: string; paidAt: string | null; invoiceNo: string }>;
  users: Array<{ id: string; name: string; email: string; username?: string | null; role: string; posRole?: string | null; status: string; joinedAt: string | null }>;
  domains: Array<{ host: string; isPrimary: boolean; dnsStatus: string; tlsStatus: string }>;
  /** POS business locations from the Postgres mirror (refreshed every 15 min by the POS→Postgres cron). */
  locations: Array<{ id: string; laravelLocationId: number | null; name: string; isActive: boolean; mirroredAt: string | null }>;
  audit: Array<{
    id: string; action: string; category: string; severity: string; outcome: string;
    subjectType: string | null; actorEmail: string; actorRole: string | null; occurredAt: string | null;
    diff: Record<string, { from: unknown; to: unknown }> | null;
    metadata: Record<string, unknown> | null;
  }>;
};
export const useAdminBusiness = (id: string) =>
  useQuery({ queryKey: ['admin', 'business', id], queryFn: () => apiGet<AdminBusinessDetail>(`/admin/businesses/${id}`), enabled: !!id });

// ── Business-DB extraction (move a business off shared/legacy MySQL onto its own
// dedicated database) — GET status + POST request only. Wire shape is the EXACT
// serializer in saas/apps/api/src/extraction/extraction-api.ts: dates are ISO strings
// (never Date objects), and there is NO verifyReport/mediaReport field on this DTO —
// those stay server-side; this surface is request+status only. Execution itself
// (preflight → freeze → copy → verify → cutover) is deliberately unreachable from any
// HTTP endpoint — see that file's module docstring for why.
export type ExtractionState =
  | 'requested' | 'preflight' | 'provisioning' | 'frozen' | 'copying' | 'verifying'
  | 'cutover' | 'done' | 'aborted' | 'rolled_back' | 'declined';
export interface BusinessExtraction {
  publicId: string;
  state: ExtractionState;
  sourceSchema: string;
  destSchema: string;
  error: string | null;
  requestedAt: string | null;
  frozenAt: string | null;
  cutoverAt: string | null;
  /** Set once the platform approves the request; the row stays 'requested' after
   *  approval (see extraction-api.ts's approveExtraction) — "is it approved?" is
   *  `approvedAt !== null`, never a state check. */
  approvedAt: string | null;
  /** YYYY-MM-DD, the night the scheduler runs this extraction. Fixed 23:30 Asia/Dhaka
   *  slot — the requester/approver picks only the date. */
  scheduledFor: string | null;
  /** Nights the scheduler's gate has deferred this row (spec §5). */
  deferralCount: number;
}
export interface BusinessExtractionStatus {
  current: BusinessExtraction | null;
  history: BusinessExtraction[];
}
const MOCK_EXTRACTION_STATUS: BusinessExtractionStatus = { current: null, history: [] };
export const getBusinessExtraction = (id: string) =>
  USE_MOCKS ? mockDelay(MOCK_EXTRACTION_STATUS) : apiGet<BusinessExtractionStatus>(`/admin/businesses/${id}/extraction`);
export const useBusinessExtraction = (id: string) =>
  useQuery({ queryKey: ['admin', 'business', id, 'extraction'], queryFn: () => getBusinessExtraction(id), enabled: !!id });
/** POST /admin/businesses/:id/extraction/request — creates the 'requested' row only.
 *  400s on the wrong acknowledgement, a non-legacy/shared dbMode, or an already
 *  requested/active extraction; the caller surfaces the server's own message. */
export const requestBusinessExtraction = (id: string, body: { acknowledgement: string }) =>
  USE_MOCKS
    ? mockDelay<BusinessExtraction>({
        publicId: 'ext_mock', state: 'requested', sourceSchema: 'ogrogoti_master', destSchema: 'pending',
        error: null, requestedAt: new Date().toISOString(), frozenAt: null, cutoverAt: null,
        approvedAt: null, scheduledFor: null, deferralCount: 0,
      })
    : apiPost<BusinessExtraction>(`/admin/businesses/${id}/extraction/request`, body);

/** One extraction waiting on approval, or approved and waiting for its night. */
export interface PendingExtraction {
  publicId: string;
  businessPublicId: string;
  businessName: string;
  sourceSchema: string;
  requestedAt: string | null;
  approved: boolean;
  scheduledFor: string | null;
}
/** GET /admin/extractions/pending — the platform-wide queue behind the nav badge.
 *  Exists because approve/decline used to live ONLY on a single business's Extraction tab,
 *  so a request could not be found without already knowing which business made it. */
export const getPendingExtractions = () =>
  USE_MOCKS ? mockDelay<PendingExtraction[]>([]) : apiGet<PendingExtraction[]>('/admin/extractions/pending');
export const usePendingExtractions = () =>
  useQuery({ queryKey: ['admin', 'extractions', 'pending'], queryFn: getPendingExtractions, staleTime: 60_000 });

/** POST /admin/extractions/:publicId/approve — sets approvedAt + scheduledFor; state
 *  stays 'requested' (extraction-api.ts's approveExtraction). Platform-only. */
export const approveBusinessExtraction = (publicId: string, scheduledFor: string) =>
  USE_MOCKS
    ? mockDelay<BusinessExtraction>({
        publicId, state: 'requested', sourceSchema: 'ogrogoti_master', destSchema: 'pending',
        error: null, requestedAt: new Date().toISOString(), frozenAt: null, cutoverAt: null,
        approvedAt: new Date().toISOString(), scheduledFor, deferralCount: 0,
      })
    : apiPost<BusinessExtraction>(`/admin/extractions/${publicId}/approve`, { scheduledFor });

/** POST /admin/extractions/:publicId/decline — reason required; terminal, frees the
 *  business for a future request (extraction-api.ts's declineExtraction). Platform-only. */
export const declineBusinessExtraction = (publicId: string, reason: string) =>
  USE_MOCKS
    ? mockDelay<BusinessExtraction>({
        publicId, state: 'declined', sourceSchema: 'ogrogoti_master', destSchema: 'pending',
        error: `declined: ${reason}`, requestedAt: new Date().toISOString(), frozenAt: null, cutoverAt: null,
        approvedAt: null, scheduledFor: null, deferralCount: 0,
      })
    : apiPost<BusinessExtraction>(`/admin/extractions/${publicId}/decline`, { reason });

export const useResellers = () => useQuery({ queryKey: ['admin', 'resellers'], queryFn: listResellers });
export const useResellerApplications = () => useQuery({ queryKey: ['admin', 'applications'], queryFn: listResellerApplications });
export const usePendingApplicationsCount = () => useQuery({ queryKey: ['admin', 'applications', 'count'], queryFn: getPendingApplicationsCount });
export const useResellerApplication = (id: string | null) =>
  useQuery({ queryKey: ['admin', 'applications', id], queryFn: () => getResellerApplication(id!), enabled: !!id });
export const useAdminUsers = () => useQuery({ queryKey: ['admin', 'users'], queryFn: listAdminUsers });
export const useAdminPayments = () => useQuery({ queryKey: ['admin', 'payments'], queryFn: listAdminPayments });
export const usePackageSubscribers = (packageId: string) =>
  useQuery({ queryKey: ['admin', 'packageSubscribers', packageId], queryFn: () => listPackageSubscribers(packageId), enabled: !!packageId });
export const useSystemHealth = () => useQuery({ queryKey: ['admin', 'health'], queryFn: getSystemHealth });
export const useWebhookDeliveries = () => useQuery({ queryKey: ['admin', 'webhooks'], queryFn: listWebhookDeliveries });
export const useCashCandidates = (q: string) =>
  useQuery({
    queryKey: ['admin', 'cashEntry', q.trim()],
    queryFn: () => searchCashCandidates(q),
    enabled: q.trim().length >= 2,
  });
export const useResellerPayments = () => useQuery({ queryKey: ['admin', 'resellerPayments'], queryFn: getResellerPayments });
export const usePlatformKpis = () => useQuery({ queryKey: ['admin', 'kpis'], queryFn: getPlatformKpis });
export const usePlatformActivity = () => useQuery({ queryKey: ['admin', 'activity'], queryFn: getPlatformActivity });
export const useTopResellers = () => useQuery({ queryKey: ['admin', 'topResellers'], queryFn: getTopResellers });
export const useBusinessesByStatus = () => useQuery({ queryKey: ['admin', 'businessesByStatus'], queryFn: getBusinessesByStatus });
export const usePlatformPricingTiers = () => useQuery({ queryKey: ['admin', 'pricingTiers'], queryFn: listPlatformPricingTiers });
export const useResellerSubscriptions = () => useQuery({ queryKey: ['admin', 'resellerSubscriptions'], queryFn: getResellerSubscriptions });
export const useResellerPackages = () => useQuery({ queryKey: ['admin', 'resellerPackages'], queryFn: getResellerPackages });
export const useResellerPayouts = () => useQuery({ queryKey: ['admin', 'resellerPayouts'], queryFn: getResellerPayouts });
export const useResellerPayoutConfig = () => useQuery({ queryKey: ['admin', 'resellerPayoutConfig'], queryFn: getResellerPayoutConfig });
export const useResellerFloorOverrides = () => useQuery({ queryKey: ['admin', 'resellerFloorOverrides'], queryFn: getResellerFloorOverrides });
/** Admin-scoped reseller revenue (the reseller's own /reseller/revenue 403s under an admin token). */
export const useAdminResellerRevenue = (resellerId: string) =>
  useQuery({
    queryKey: ['admin', 'reseller', resellerId, 'revenue'],
    queryFn: () =>
      USE_MOCKS ? mockDelay(resellerRevenue) : apiGet<typeof resellerRevenue>(`/admin/resellers/${resellerId}/revenue`),
    enabled: !!resellerId,
  });

/* Pure floor-resolution helper + fixed global floor constants (MAP policy,
 * Decision #20) re-exported so pages import them from the seam, not mocks/. */
export {
  resolveFloor,
  GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT,
  GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT,
} from '../mocks/admin';

// ── Admin mutations (money-loop + lifecycle) ─────────────────────────────────
// Real-API in real mode; mock mode resolves a no-op success so the action isn't
// a dead button there. Callers invalidate the relevant query on success.

/** Application decision/transition — POST /admin/reseller-applications/:id/decision.
 *  approve/reject are terminal; in_review / more_info / note / reverse drive the review workflow. */
export const decideResellerApplication = (
  id: string,
  body: { decision: 'approve' | 'reject' | 'in_review' | 'more_info' | 'note' | 'reverse'; commissionPct?: number; note?: string },
) => (USE_MOCKS ? mockDelay({ ok: true }) : apiPost(`/admin/reseller-applications/${id}/decision`, body));

/** Invite a platform teammate (link + email) — POST /admin/users. Returns a copyable accept link. */
export type InviteResult = { ok: boolean; inviteUrl: string; email: string; expiresAt?: string };
/** Result of `action: 'create'` — the account exists immediately (no invite link). */
export type CreateUserResult = { ok: boolean; action: 'create'; userId: number; email: string; created: boolean; passwordSet: boolean };
/**
 * Add a platform teammate — POST /admin/users. `action: 'invite'` (default) emails
 * a secure accept link; `action: 'create'` sets a password now (min 8) so they can
 * sign in immediately. Returns the invite link on invite, the created user on create.
 */
export const inviteAdminUser = (
  body: { action?: 'create' | 'invite'; email: string; name?: string; role: 'platform_admin' | 'platform_staff'; password?: string },
): Promise<InviteResult | CreateUserResult> =>
  USE_MOCKS
    ? body.action === 'create'
      ? mockDelay<CreateUserResult>({ ok: true, action: 'create', userId: 1, email: body.email, created: true, passwordSet: true })
      : mockDelay<InviteResult>({ ok: true, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token`, email: body.email })
    : apiPost<InviteResult | CreateUserResult>('/admin/users', body);

/**
 * Admin: add a user to a SPECIFIC business (Create or Invite) — POST /businesses/:id/team.
 * RLS authorizes a platform admin to create for any business; the chosen POS role maps to the
 * Laravel role (Admin#<lbid> / Cashier#<lbid>) when the user is pushed into the business's POS DB.
 */
export const createAdminBusinessUser = (
  businessPublicId: string,
  body: { action?: 'create' | 'invite'; email: string; name?: string; posRole?: string; password?: string; username?: string },
): Promise<InviteResult | CreateUserResult> =>
  USE_MOCKS
    ? body.action === 'create'
      ? mockDelay<CreateUserResult>({ ok: true, action: 'create', userId: 1, email: body.email, created: true, passwordSet: true })
      : mockDelay<InviteResult>({ ok: true, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token`, email: body.email })
    : apiPost<InviteResult | CreateUserResult>(`/businesses/${businessPublicId}/team`, body);

/** Package-editor writes: add a price row; replace purchasable add-on links. */
export const addPackagePrice = (
  packageId: string,
  input: { currencyCode?: string; interval: 'month' | 'quarter' | 'year' | 'one_time'; amountMinor: number; trialDays?: number; status?: 'draft' | 'active'; prorationEnabled?: boolean; billingAnchor?: 'subscription_start' | 'first_of_month' },
) => (USE_MOCKS ? mockDelay({ id: 'price_mock', ...input }) : apiPost(`/admin/packages/${packageId}/prices`, input));
export const setPackageAddOns = (packageId: string, addOnIds: string[]) =>
  USE_MOCKS ? mockDelay({ ok: true, linked: addOnIds.length }) : apiPut(`/admin/packages/${packageId}/add-ons`, { addOnIds });

/** Pricing-tier create/edit — POST/PATCH /admin/pricing-tiers. Money in MINOR units. */
export type PricingTierInput = {
  slug: string;
  displayName: string;
  description?: string;
  minMonthlyMinor: number;
  minYearlyMinor: number;
  hintMaxLocations?: number | null;
  hintMaxUsers?: number | null;
  hintMaxProducts?: number | null;
  isActive?: boolean;
};
export const addPricingTier = (input: PricingTierInput) =>
  USE_MOCKS
    ? mockDelay({ ...input, id: `pt_${input.slug}`, defaultMinMonthlyMinor: input.minMonthlyMinor, defaultMinYearlyMinor: input.minYearlyMinor, sortOrder: 99, packageCount: 0 })
    : apiPost(`/admin/pricing-tiers`, input);
export const updatePricingTier = (id: string, patch: Partial<Omit<PricingTierInput, 'slug'>>) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/pricing-tiers/${id}`, patch);

/* ── User moderation (admin UserDetail) — all target-scoped, all real ──────── */
export type AdminUserSessionRow = {
  id: string;
  deviceLabel: string;
  deviceIcon: string;
  ip: string | null;
  geoCountry: string | null;
  geoCity: string | null;
  userAgent: string | null;
  createdAt: string | null;
  lastActiveAt: string | null;
};
export const updateAdminUser = (id: string, patch: { status?: 'active' | 'suspended' | 'deactivated'; maxActiveSessionsOverride?: number | null; name?: string; email?: string }) =>
  USE_MOCKS ? mockDelay({ ok: true, status: patch.status ?? 'active' }) : apiPatch<{ ok: true; status: string }>(`/admin/users/${id}`, patch);

/** Edit a business's core info (name / owner email) — PATCH /admin/businesses/:id. */
export const updateAdminBusinessInfo = (id: string, patch: { name?: string; ownerEmail?: string | null }) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch<{ ok: boolean }>(`/admin/businesses/${id}`, patch);

// Member edit/removal ride the shared, reseller-capable endpoints — see
// updateTeamMember/removeTeamMember in api/resources.ts (PATCH/DELETE /businesses/:id/team/:userId).
export const adminResetUserPassword = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true, email: 'user@example.com' }) : apiPost<{ ok: true; email: string }>(`/admin/users/${id}/reset-password`, {});
export const adminForceSignout = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPost(`/admin/users/${id}/force-signout`, {});
export const listUserSessions = (id: string) =>
  USE_MOCKS ? mockDelay<AdminUserSessionRow[]>([]) : apiGetList<AdminUserSessionRow>(`/admin/users/${id}/sessions`);
export const useUserSessions = (id: string) =>
  useQuery({ queryKey: ['admin', 'user', id, 'sessions'], queryFn: () => listUserSessions(id), enabled: !!id });
export const revokeUserSessionAdmin = (id: string, sessionId: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/admin/users/${id}/sessions/${sessionId}`);
export const adminDisableUser2fa = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiDelete(`/admin/users/${id}/2fa`);

/** Create a reseller + invite its owner — POST /admin/resellers. Returns the copyable accept link. */
export const addReseller = (input: { name: string; slug: string; ownerEmail: string; ownerName?: string; commissionPct?: number }) =>
  USE_MOCKS
    ? mockDelay({ id: 'rs_mock', name: input.name, slug: input.slug, email: input.ownerEmail, inviteUrl: `${window.location.origin}/accept-invite?token=mock-token` })
    : apiPost<{ id: string; name: string; slug: string; email: string; inviteUrl: string }>('/admin/resellers', input);

/** Set a reseller's lifecycle status — PATCH /admin/resellers/:id. */
export const setResellerStatus = (
  id: string,
  status: 'invited' | 'pending_setup' | 'active' | 'frozen' | 'terminated',
) => (USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/resellers/${id}`, { status }));

/** Set a reseller's platform commission % — PATCH /admin/resellers/:id. Takes effect on
 *  FUTURE payments only: billing reads resellers.commissionPct live at credit time and
 *  snapshots it per ledger entry, so past accruals/refunds keep their original rate. */
export const setResellerCommission = (id: string, commissionPct: number) =>
  USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/resellers/${id}`, { commissionPct });

/** Mint a read-only, audited impersonation token for a reseller — POST /admin/resellers/:id/impersonate ("Open as reseller"). */
export type ImpersonationResult = { accessToken: string; target: { id: string; name: string; role: 'reseller' } };
export const startResellerImpersonation = (id: string): Promise<ImpersonationResult> =>
  USE_MOCKS
    ? mockDelay({ accessToken: 'mock-imp', target: { id, name: 'Acme Resellers', role: 'reseller' } })
    : apiPost<ImpersonationResult>(`/admin/resellers/${id}/impersonate`);

/** Set a business's lifecycle status — PATCH /admin/businesses/:id. */
export const setBusinessStatus = (
  id: string,
  status: 'pending_payment' | 'provisioning' | 'active' | 'past_due' | 'suspended' | 'canceled' | 'deleted',
) => (USE_MOCKS ? mockDelay({ ok: true }) : apiPatch(`/admin/businesses/${id}`, { status }));

/** Void the business's OPEN renewal invoice(s) — POST /admin/businesses/:id/void-open-invoice.
 *  The nightly cron reissues a fresh invoice at the then-current plan price. */
export const voidOpenInvoice = (id: string) =>
  USE_MOCKS ? mockDelay({ ok: true, voided: ['INV-0000'] }) : apiPost<{ ok: boolean; voided: string[] }>(`/admin/businesses/${id}/void-open-invoice`, {});

/** Change a business's subscription plan — POST /admin/businesses/:id/change-plan.
 *  Entitlements re-freeze from the new package now; the new price bills from the next renewal (no proration). */
export const changeBusinessPlan = (id: string, body: { packageId: string; interval: 'month' | 'year'; reason: string }) =>
  USE_MOCKS
    ? mockDelay({ ok: true, plan: 'Pro', interval: body.interval, amountMinor: 0 })
    : apiPost<{ ok: boolean; plan: string; interval: string; amountMinor: number }>(`/admin/businesses/${id}/change-plan`, body);

/** Admin billable upgrade — POST /admin/businesses/:id/upgrade-invoice. Creates an
 *  open invoice at the new price; the plan changes only once it's paid (owner
 *  self-pay via the existing Billing page, or admin cash-against-this-invoice). */
export const createBillableUpgradeInvoice = (id: string, body: { packageId: string; interval: 'month' | 'year' }) =>
  USE_MOCKS
    ? mockDelay({ ok: true, invoiceNo: 'INV-MOCK-0002', amountMinor: 300000 })
    : apiPost<{ ok: boolean; invoiceNo: string; amountMinor: number }>(`/admin/businesses/${id}/upgrade-invoice`, body);

/** Refund/chargeback a payment (claws back reseller commission) — POST /admin/payments/:id/refund. */
export const refundPayment = (
  id: string,
  body: { amountMinor: number; reason?: string; channel?: 'refund' | 'chargeback' },
) => (USE_MOCKS ? mockDelay({ ok: true }) : apiPost(`/admin/payments/${id}/refund`, body));

// ── Platform settings (GET + PATCH the editable defaults) ────────────────────
export type AdminBranding = {
  brandName: string;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  supportEmail: string;
  supportPhone: string | null;
};
/**
 * ITCity's own brand. The parent company (itcity.ltd / itcitysoft.com) and the Possignin
 * product (possignin.com) are separate public identities, so they get separate slots —
 * one shared blob meant a favicon uploaded for one site appeared on the other. Same
 * editable fields as `AdminBranding`; each falls back to the built-in identity when blank.
 */
export type AdminBrandingItcity = {
  brandName: string;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  supportEmail: string;
  supportPhone: string | null;
};
export type AdminSettings = {
  maxActiveSessionsDefault: number;
  signupOtpRequired: boolean;
  signupOtpDeliveryDefault: string;
  resellerTwoFaRequiredDefault: boolean;
  platformAdminTwoFaRequired: boolean;
  activeCurrencies: string[];
  defaultCurrency: string;
  defaultTimezone: string;
  termsUrl: string | null;
  privacyUrl: string | null;
  allowResellerSignup: boolean;
  allowDirectSignup: boolean;
  email: {
    fromName: string;
    fromAddress: string;
    replyTo: string | null;
    smtp: { host: string | null; port: number; secure: boolean; username: string | null; passwordSet: boolean };
    /** Which transport is ACTUALLY sending (server-resolved), so the tab can say
     *  when its own SMTP block is stored but dormant behind a Mailgun env config. */
    active: { driver: 'mailgun' | 'smtp' | 'log'; source: string; fromAddress: string | null };
  };
  /** Transactional SMS gateway state — env-driven (BOOMCAST_*), read-only here. */
  sms: { driver: 'boomcast' | 'log'; senderId: string | null };
  /** The Possignin product brand (possignin.com) + the default resellers white-label over. */
  branding: AdminBranding;
  /** ITCity the company (itcity.ltd, itcitysoft.com). */
  brandingItcity: AdminBrandingItcity;
};
const MOCK_SETTINGS: AdminSettings = {
  maxActiveSessionsDefault: 5, signupOtpRequired: true, signupOtpDeliveryDefault: 'both',
  resellerTwoFaRequiredDefault: false, platformAdminTwoFaRequired: true,
  activeCurrencies: ['BDT', 'USD', 'GBP', 'EUR'], defaultCurrency: 'BDT',
  defaultTimezone: 'Asia/Dhaka', termsUrl: null, privacyUrl: null,
  allowResellerSignup: true, allowDirectSignup: true,
  email: {
    fromName: 'Possignin', fromAddress: 'no-reply@itcitysoft.com', replyTo: 'support@itcitysoft.com',
    smtp: { host: null, port: 587, secure: false, username: null, passwordSet: false },
    active: { driver: 'log', source: 'No transport configured — mail is written to the application log only', fromAddress: null },
  },
  sms: { driver: 'log', senderId: null },
  branding: {
    brandName: 'Possignin', logoLightUrl: null, logoDarkUrl: null, faviconUrl: null,
    primaryColor: '#4F46E5', supportEmail: 'support@possignin.com', supportPhone: null,
  },
  brandingItcity: {
    brandName: 'ITCity', logoLightUrl: null, logoDarkUrl: null, faviconUrl: null,
    primaryColor: '#4F46E5', supportEmail: 'hello@itcitysoft.com', supportPhone: null,
  },
};
export const getAdminSettings = () =>
  USE_MOCKS ? mockDelay(MOCK_SETTINGS) : apiGet<AdminSettings>('/admin/settings');
export const useAdminSettings = () => useQuery({ queryKey: ['admin', 'settings'], queryFn: getAdminSettings });
export const updateAdminSettings = (
  patch: Partial<
    Pick<
      AdminSettings,
      | 'maxActiveSessionsDefault'
      | 'signupOtpRequired'
      | 'platformAdminTwoFaRequired'
      | 'defaultCurrency'
      | 'defaultTimezone'
      | 'termsUrl'
      | 'privacyUrl'
      | 'allowResellerSignup'
      | 'allowDirectSignup'
    >
  > & {
    branding?: Partial<AdminBranding>;
    brandingItcity?: Partial<AdminBrandingItcity>;
    email?: {
      fromName?: string;
      fromAddress?: string;
      replyTo?: string | null;
      smtp?: { host?: string | null; port?: number; secure?: boolean; username?: string | null; password?: string };
    };
  },
) => (USE_MOCKS ? mockDelay(MOCK_SETTINGS) : apiPatch<AdminSettings>('/admin/settings', patch));

// ── Full-database export ─────────────────────────────────────────────────────────────
// Download a managed business's OWN database as <dbname>.sql.gz. Only 'managed'
// businesses have a database of their own: a legacy/shared business's rows live in a
// schema shared with 60+ others, so the server refuses it (400) rather than handing one
// customer everyone else's data. Resellers reach the same file through a request the
// platform must approve — these are the platform-side controls for that queue.
export type DbExportState = 'requested' | 'approved' | 'declined' | 'revoked';

export interface DbExportRequest {
  publicId: string;
  businessId: number;
  schemaName: string;
  state: DbExportState;
  reason: string;
  /** 'owner' | 'reseller' — WHO asked. An approval is scoped to this, so the same business
   *  can have one of each, and neither party can spend the other's approval. */
  requestedByKind: 'owner' | 'reseller';
  decidedAt: string | null;
  decisionNote: string | null;
  /** Computed server-side (decidedAt + 48h); null unless state is 'approved'. */
  expiresAt: string | null;
  downloadCount: number;
  lastDownloadedAt: string | null;
  createdAt: string;
}

/**
 * Fetch the dump and hand the browser a file.
 *
 * This cannot be a plain <a href>: the API authenticates with a Bearer header, not a
 * cookie, so a bare link arrives unauthenticated. The whole file lands in memory as a
 * blob — acceptable at the measured 24.5 MB worst case (the largest dedicated database
 * on the platform), and the alternative (a signed one-time URL) would mean a second
 * auth mechanism for one button.
 */
export async function downloadBusinessDatabase(id: string, dbName: string): Promise<void> {
  const res = await apiFetchRaw(`/admin/businesses/${id}/database/download`);
  if (!res.ok) {
    let message = 'The download could not be started.';
    try { message = (await res.json())?.error?.message ?? message; } catch { /* non-JSON error body */ }
    throw new Error(message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dbName}.sql.gz`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const getBusinessDbExport = (id: string) =>
  apiGet<{ current: DbExportRequest | null; history: DbExportRequest[] }>(`/admin/businesses/${id}/database/export`);
export const useBusinessDbExport = (id: string, enabled = true) =>
  useQuery({
    queryKey: ['admin', 'business', id, 'db-export'],
    queryFn: () => getBusinessDbExport(id),
    enabled: !!id && enabled && !USE_MOCKS,
  });

export const approveDbExport = (publicId: string) =>
  apiPost<DbExportRequest>(`/admin/database-exports/${publicId}/approve`, {});
export const declineDbExport = (publicId: string, note: string) =>
  apiPost<DbExportRequest>(`/admin/database-exports/${publicId}/decline`, { note });
export const revokeDbExport = (publicId: string, note: string) =>
  apiPost<DbExportRequest>(`/admin/database-exports/${publicId}/revoke`, { note });
